import React, {
  useCallback,
  useMemo,
} from "react";

import {
  useSelector,
} from "react-redux";

import {
  useField,
} from "formik";

import {
  styled,
} from "@mui/material/styles";

import {
  Autocomplete,
  FormHelperText,
  InputLabel,
  TextField,
} from "@mui/material";

import {
  useGetCountriesQuery,
} from "../../../redux/features/countryApi";

const TextFieldStyled = styled(
  TextField
)(({ theme }) => ({
  "label + &": {
    marginTop: theme.spacing(0),
  },

  "& .MuiInputBase-input": {
    borderRadius: 4,
    backgroundColor: "#f5f5f5",
    fontSize: 16,
    fontWeight: 500,

    transition:
      theme.transitions.create([
        "border-color",
        "background-color",
        "box-shadow",
      ]),

    fontFamily: [
      "-apple-system",
      "BlinkMacSystemFont",
      '"Segoe UI"',
      "Roboto",
      '"Helvetica Neue"',
      "Arial",
      "sans-serif",
      '"Apple Color Emoji"',
      '"Segoe UI Emoji"',
      '"Segoe UI Symbol"',
    ].join(","),
  },
}));

const getOptionIdentity = (
  option
) => {
  if (
    option === null ||
    option === undefined
  ) {
    return null;
  }

  if (
    typeof option !== "object"
  ) {
    return option;
  }

  return (
    option.id ??
    option.uf ??
    option.code ??
    option.value ??
    option.currencycode ??
    null
  );
};

const findOptionByValue = (
  options,
  value
) => {
  if (
    value === null ||
    value === undefined ||
    value === ""
  ) {
    return null;
  }

  if (
    typeof value === "object"
  ) {
    return value;
  }

  return (
    options.find((option) => {
      const optionIdentity =
        getOptionIdentity(option);

      if (
        optionIdentity === null
      ) {
        return false;
      }

      return (
        String(optionIdentity) ===
        String(value)
      );
    }) ?? null
  );
};

const getPersonLabel = (
  option
) => {
  return [
    option?.first_name,
    option?.last_name,
  ]
    .filter(Boolean)
    .join(" ");
};

const AutocompleteWrapper = ({
  name,
  label,
  required = false,
  disabled = false,

  /*
   * Optional explicit options.
   * When supplied, these have priority over
   * Redux and RTK Query sources.
   */
  options: providedOptions,

  onChange: externalOnChange,
  onBlur: externalOnBlur,

  sx,
  ...textFieldProps
}) => {
  const [
    field,
    meta,
    helpers,
  ] = useField(String(name));

  const fieldName =
    field.name;

  const isCountryField =
    fieldName.startsWith(
      "country"
    );

  const {
    data: countries = [],
    isFetching:
      isFetchingCountries,
  } = useGetCountriesQuery(
    undefined,
    {
      skip:
        !isCountryField ||
        Array.isArray(
          providedOptions
        ),
    }
  );

  /*
   * These option sources still use legacy Redux.
   * They can be migrated separately.
   */
  const persons =
    useSelector(
      (state) =>
        state.personSlice
          ?.persons ?? []
    );

  const statuses =
    useSelector(
      (state) =>
        state.statusSlice
          ?.statuses ?? []
    );

  const contacts =
    useSelector(
      (state) =>
        state.contactSlice
          ?.contacts ?? []
    );

  const currencies =
    useSelector(
      (state) =>
        state.currencySlice
          ?.currencies ?? []
    );

  const noteTerms =
    useSelector(
      (state) =>
        state.noteTermSlice
          ?.noteTerms ?? []
    );

  const contractTerms =
    useSelector(
      (state) =>
        state.contractTermSlice
          ?.contractTerms ?? []
    );

  const itemsForItemInv =
    useSelector(
      (state) =>
        state.itemForItemInvSlice
          ?.itemsForItemInv ?? []
    );

  const options = useMemo(() => {
    if (
      Array.isArray(
        providedOptions
      )
    ) {
      return providedOptions;
    }

    if (
      fieldName === "person" ||
      fieldName ===
        "personscontact" ||
      fieldName.startsWith(
        "personscontact["
      )
    ) {
      return persons;
    }

    if (
      fieldName === "stype" ||
      fieldName === "status"
    ) {
      return statuses;
    }

    if (
      fieldName === "currency" ||
      fieldName ===
        "currencycode"
    ) {
      return currencies;
    }

    if (isCountryField) {
      return countries;
    }

    if (
      fieldName ===
      "note_terms"
    ) {
      return noteTerms;
    }

    if (
      fieldName ===
      "contract_terms"
    ) {
      return contractTerms;
    }

    if (
      fieldName.startsWith(
        "iteminv_invs"
      )
    ) {
      return itemsForItemInv;
    }

    return contacts;
  }, [
    contacts,
    contractTerms,
    countries,
    currencies,
    fieldName,
    isCountryField,
    itemsForItemInv,
    noteTerms,
    persons,
    providedOptions,
    statuses,
  ]);

  const selectedValue =
    useMemo(() => {
      return findOptionByValue(
        options,
        field.value
      );
    }, [
      field.value,
      options,
    ]);

  const getOptionLabel =
    useCallback(
      (rawOption) => {
        const option =
          findOptionByValue(
            options,
            rawOption
          ) ?? rawOption;

        if (
          option === null ||
          option === undefined
        ) {
          return "";
        }

        if (
          typeof option !==
          "object"
        ) {
          return String(option);
        }

        if (
          fieldName === "person" ||
          fieldName ===
            "personscontact" ||
          fieldName.startsWith(
            "personscontact["
          )
        ) {
          return getPersonLabel(
            option
          );
        }

        if (
          fieldName === "stype"
        ) {
          return String(
            option.st ?? ""
          );
        }

        if (
          fieldName === "htype"
        ) {
          return String(
            option.ht ?? ""
          );
        }

        if (
          fieldName === "mtype"
        ) {
          return String(
            option.mt ?? ""
          );
        }

        if (
          fieldName ===
            "currency" ||
          fieldName ===
            "currencycode"
        ) {
          return String(
            option.currencycode ??
              ""
          );
        }

        if (isCountryField) {
          return String(
            option.label ??
              option.value ??
              ""
          );
        }

        if (
          fieldName ===
          "note_terms"
        ) {
          return String(
            option.note_short ??
              ""
          );
        }

        if (
          fieldName ===
          "contract_terms"
        ) {
          return String(
            option.term_short ??
              ""
          );
        }

        if (
          fieldName.startsWith(
            "iteminv_invs"
          )
        ) {
          const description =
            option.description ??
            "";

          const code =
            option.code
              ? ` - ${option.code}`
              : "";

          return `${description}${code}`;
        }

        return String(
          option.companyname ??
            option.label ??
            option.name ??
            option.value ??
            ""
        );
      },
      [
        fieldName,
        isCountryField,
        options,
      ]
    );

  const isOptionEqualToValue =
    useCallback(
      (option, value) => {
        const optionIdentity =
          getOptionIdentity(
            option
          );

        const valueIdentity =
          getOptionIdentity(
            value
          );

        if (
          optionIdentity === null ||
          valueIdentity === null
        ) {
          return (
            option === value
          );
        }

        return (
          String(optionIdentity) ===
          String(valueIdentity)
        );
      },
      []
    );

  const handleChange =
    useCallback(
      (
        event,
        value,
        reason,
        details
      ) => {
        helpers.setValue(
          value
        );

        externalOnChange?.(
          event,
          value,
          reason,
          details
        );
      },
      [
        externalOnChange,
        helpers,
      ]
    );

  const handleBlur =
    useCallback(
      (event) => {
        helpers.setTouched(
          true
        );

        externalOnBlur?.(
          event
        );
      },
      [
        externalOnBlur,
        helpers,
      ]
    );

  const hasError =
    Boolean(
      meta.touched &&
        meta.error
    );

  const helperText =
    hasError &&
    typeof meta.error ===
      "string"
      ? meta.error
      : "";

  return (
    <Autocomplete
      disabled={disabled}
      loading={
        isCountryField &&
        isFetchingCountries
      }
      options={options}
      value={selectedValue}
      onChange={handleChange}
      onBlur={handleBlur}
      getOptionLabel={
        getOptionLabel
      }
      isOptionEqualToValue={
        isOptionEqualToValue
      }
      renderInput={(
        params
      ) => (
        <>
          <InputLabel
            htmlFor={
              fieldName
            }
            required={
              required
            }
            shrink
            sx={{
              mb: -1,
              fontSize: 18,
              fontWeight:
                "bold",
            }}
          >
            {label}
          </InputLabel>

          <TextFieldStyled
            {...params}
            {...textFieldProps}
            id={fieldName}
            name={fieldName}
            fullWidth
            variant="outlined"
            size="small"
            required={required}
            error={hasError}
            autoComplete="off"
            sx={{
              backgroundColor:
                "#f5f5f5",

              ...sx,
            }}
            inputProps={{
              ...params.inputProps,
              id: fieldName,
              autoComplete:
                "new-password",
            }}
          />

          <FormHelperText
            error={hasError}
            sx={{
              color:
                hasError
                  ? "error.main"
                  : undefined,

              fontSize: 14,
              width: "100%",
            }}
          >
            {helperText}
          </FormHelperText>
        </>
      )}
    />
  );
};

export default AutocompleteWrapper;